/**
 * Storage Adapter Interface
 * Phase 5: Data Layer - Storage Abstraction
 */

export interface StorageAdapter {
  // Basic operations
  save(key: string, value: any): Promise<void>
  load(key: string): Promise<any>
  delete(key: string): Promise<void>
  list(prefix: string): Promise<string[]>

  // Export/Import
  export(): Promise<Blob>
  import(data: Blob): Promise<void>

  // Metadata
  clear(): Promise<void>
  getKeys(): Promise<string[]>
}

/**
 * Storage Registry
 * Manages multiple storage adapters
 */
export class StorageRegistry {
  private adapters: Map<string, StorageAdapter> = new Map()

  registerAdapter(name: string, adapter: StorageAdapter): void {
    this.adapters.set(name, adapter)
  }

  getAdapter(name: string): StorageAdapter | undefined {
    return this.adapters.get(name)
  }

  getDefaultAdapter(): StorageAdapter {
    const defaultAdapter = this.adapters.get('default')
    if (defaultAdapter) return defaultAdapter
    const localAdapter = this.adapters.get('local')
    if (localAdapter) return localAdapter
    const firstAdapter = this.adapters.values().next().value
    if (firstAdapter) return firstAdapter
    throw new Error('No storage adapter registered')
  }

  listAdapters(): string[] {
    return Array.from(this.adapters.keys())
  }

  // Proxy methods to default adapter
  async save(key: string, value: any): Promise<void> {
    return await this.getDefaultAdapter().save(key, value)
  }

  async load(key: string): Promise<any> {
    return await this.getDefaultAdapter().load(key)
  }

  async delete(key: string): Promise<void> {
    return await this.getDefaultAdapter().delete(key)
  }

  async list(prefix: string): Promise<string[]> {
    return await this.getDefaultAdapter().list(prefix)
  }

  async export(): Promise<Blob> {
    return await this.getDefaultAdapter().export()
  }

  async import(data: Blob): Promise<void> {
    return await this.getDefaultAdapter().import(data)
  }

  async clear(): Promise<void> {
    return await this.getDefaultAdapter().clear()
  }

  async getKeys(): Promise<string[]> {
    return await this.getDefaultAdapter().getKeys()
  }
}

/**
 * Generate a unique ID
 */
export function generateId(): string {
  return Math.random().toString(36).substr(2, 9)
}

/**
 * LocalStorage Adapter
 */
export class LocalStorageAdapter implements StorageAdapter {
  private prefix: string

  constructor(prefix: string = 'freshcards:') {
    this.prefix = prefix
  }

  private getKey(key: string): string {
    return this.prefix + key
  }

  async save(key: string, value: any): Promise<void> {
    try {
      const serialized = JSON.stringify(value)
      localStorage.setItem(this.getKey(key), serialized)
    } catch (error) {
      console.error('Failed to save to localStorage:', error)
      throw error
    }
  }

  async load(key: string): Promise<any> {
    try {
      const serialized = localStorage.getItem(this.getKey(key))
      if (!serialized) return null
      return JSON.parse(serialized)
    } catch (error) {
      console.error('Failed to load from localStorage:', error)
      return null
    }
  }

  async delete(key: string): Promise<void> {
    try {
      localStorage.removeItem(this.getKey(key))
    } catch (error) {
      console.error('Failed to delete from localStorage:', error)
      throw error
    }
  }

  async list(prefix: string): Promise<string[]> {
    try {
      const keys: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.prefix + prefix)) {
          keys.push(key.substring(this.prefix.length))
        }
      }
      return keys
    } catch (error) {
      console.error('Failed to list localStorage keys:', error)
      return []
    }
  }

  async export(): Promise<Blob> {
    try {
      const data: { [key: string]: any } = {}
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.prefix)) {
          const value = localStorage.getItem(key)
          if (value) {
            data[key.substring(this.prefix.length)] = JSON.parse(value)
          }
        }
      }
      return new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    } catch (error) {
      console.error('Failed to export localStorage:', error)
      throw error
    }
  }

  async import(data: Blob): Promise<void> {
    try {
      const text = await data.text()
      const importedData = JSON.parse(text)
      for (const [key, value] of Object.entries(importedData)) {
        localStorage.setItem(this.getKey(key), JSON.stringify(value))
      }
    } catch (error) {
      console.error('Failed to import to localStorage:', error)
      throw error
    }
  }

  async clear(): Promise<void> {
    try {
      const keysToRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.prefix)) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key))
    } catch (error) {
      console.error('Failed to clear localStorage:', error)
      throw error
    }
  }

  async getKeys(): Promise<string[]> {
    try {
      const keys: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.prefix)) {
          keys.push(key.substring(this.prefix.length))
        }
      }
      return keys
    } catch (error) {
      console.error('Failed to get localStorage keys:', error)
      return []
    }
  }
}

/**
 * IndexedDB Adapter
 */
export class IndexedDBAdapter implements StorageAdapter {
  private dbName: string
  private version: number
  private db: IDBDatabase | null = null

  constructor(dbName: string = 'freshcards', version: number = 1) {
    this.dbName = dbName
    this.version = version
  }

  async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version)
      
      request.onerror = () => reject(new Error('Failed to open IndexedDB'))
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains('freshcards')) {
          db.createObjectStore('freshcards', { keyPath: 'key' })
        }
      }
    })
  }

  private ensureDb(): IDBDatabase {
    if (!this.db) {
      throw new Error('IndexedDB not initialized')
    }
    return this.db
  }

  async save(key: string, value: any): Promise<void> {
    try {
      const db = this.ensureDb()
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['freshcards'], 'readwrite')
        const store = transaction.objectStore('freshcards')
        
        const request = store.put({ key, value })
        
        request.onerror = () => reject(new Error('Failed to save to IndexedDB'))
        request.onsuccess = () => resolve()
      })
    } catch (error) {
      console.error('Failed to save to IndexedDB:', error)
      throw error
    }
  }

  async load(key: string): Promise<any> {
    try {
      const db = this.ensureDb()
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['freshcards'], 'readonly')
        const store = transaction.objectStore('freshcards')
        
        const request = store.get(key)
        
        request.onerror = () => reject(new Error('Failed to load from IndexedDB'))
        request.onsuccess = () => {
          resolve(request.result?.value || null)
        }
      })
    } catch (error) {
      console.error('Failed to load from IndexedDB:', error)
      return null
    }
  }

  async delete(key: string): Promise<void> {
    try {
      const db = this.ensureDb()
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['freshcards'], 'readwrite')
        const store = transaction.objectStore('freshcards')
        
        const request = store.delete(key)
        
        request.onerror = () => reject(new Error('Failed to delete from IndexedDB'))
        request.onsuccess = () => resolve()
      })
    } catch (error) {
      console.error('Failed to delete from IndexedDB:', error)
      throw error
    }
  }

  async list(prefix: string): Promise<string[]> {
    try {
      const db = this.ensureDb()
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['freshcards'], 'readonly')
        const store = transaction.objectStore('freshcards')
        
        const request = store.getAllKeys()
        
        request.onerror = () => reject(new Error('Failed to list IndexedDB keys'))
        request.onsuccess = () => {
          const keys = request.result as string[]
          resolve(keys.filter(key => key.startsWith(prefix)))
        }
      })
    } catch (error) {
      console.error('Failed to list IndexedDB keys:', error)
      return []
    }
  }

  async export(): Promise<Blob> {
    try {
      const db = this.ensureDb()
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['freshcards'], 'readonly')
        const store = transaction.objectStore('freshcards')
        
        const request = store.getAll()
        
        request.onerror = () => reject(new Error('Failed to export from IndexedDB'))
        request.onsuccess = () => {
          const data: { [key: string]: any } = {}
          request.result.forEach(item => {
            data[item.key] = item.value
          })
          resolve(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }))
        }
      })
    } catch (error) {
      console.error('Failed to export from IndexedDB:', error)
      throw error
    }
  }

  async import(data: Blob): Promise<void> {
    try {
      const text = await data.text()
      const importedData = JSON.parse(text)
      
      const db = this.ensureDb()
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['freshcards'], 'readwrite')
        const store = transaction.objectStore('freshcards')
        
        // Clear existing data
        store.clear()
        
        // Add imported data
        for (const [key, value] of Object.entries(importedData)) {
          store.put({ key, value })
        }
        
        transaction.onerror = () => reject(new Error('Failed to import to IndexedDB'))
        transaction.oncomplete = () => resolve()
      })
    } catch (error) {
      console.error('Failed to import to IndexedDB:', error)
      throw error
    }
  }

  async clear(): Promise<void> {
    try {
      const db = this.ensureDb()
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['freshcards'], 'readwrite')
        const store = transaction.objectStore('freshcards')
        
        const request = store.clear()
        
        request.onerror = () => reject(new Error('Failed to clear IndexedDB'))
        request.onsuccess = () => resolve()
      })
    } catch (error) {
      console.error('Failed to clear IndexedDB:', error)
      throw error
    }
  }

  async getKeys(): Promise<string[]> {
    try {
      const db = this.ensureDb()
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['freshcards'], 'readonly')
        const store = transaction.objectStore('freshcards')
        
        const request = store.getAllKeys()
        
        request.onerror = () => reject(new Error('Failed to get IndexedDB keys'))
        request.onsuccess = () => {
          resolve(request.result as string[])
        }
      })
    } catch (error) {
      console.error('Failed to get IndexedDB keys:', error)
      return []
    }
  }
}

/**
 * Initialize storage adapters
 */
export async function initializeStorage(): Promise<void> {
  // Create and register LocalStorage adapter
  const localStorageAdapter = new LocalStorageAdapter()
  storage.registerAdapter('local', localStorageAdapter)
  
  // Create and register IndexedDB adapter
  const indexedDBAdapter = new IndexedDBAdapter()
  await indexedDBAdapter.initialize()
  storage.registerAdapter('indexeddb', indexedDBAdapter)
  
  // Set LocalStorage as default adapter
  storage.registerAdapter('default', localStorageAdapter)
}

/**
 * Register a FilesystemStorageAdapter against the given registry.
 *
 * Lazy registration helper — does NOT run at boot. The host calls this
 * explicitly with the workspaceRoot of the project to back FreshCards
 * with a filesystem source (per F 40 SHIPPED design).
 *
 * The filesystem adapter is read-only in v1; `save`/`delete`/`import`/`clear`
 * throw FilesystemReadOnlyError. See `src/core/adapters/filesystem/README.md`
 * for the full contract.
 */
import type { FilesystemAdapterOptions } from './adapters/filesystem/types'
export type { FilesystemAdapterOptions }

export async function registerFilesystemAdapter(
  registry: StorageRegistry,
  opts: FilesystemAdapterOptions,
): Promise<void> {
  // F 59 fix: the .js extension is required for Node ESM resolution in lib
  // mode. Vite dev server resolves without extension but Node's ESM loader
  // requires explicit extensions. The prepare script (see package.json)
  // bundles this path via esbuild to dist/adapters/filesystem/filesystem-adapter.js.
  // For SPA/dev mode, the relative path also works because Vite dev server
  // handles extension-less imports.
  const modulePath = ['./adapters', 'filesystem', 'filesystem-adapter.js'].join('/')
  const mod = await import(/* @vite-ignore */ modulePath)
  const adapter = new mod.FilesystemStorageAdapter(opts)
  registry.registerAdapter('filesystem', adapter)
}

/**
 * Global storage instance
 */
export const storage = new StorageRegistry()
